import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { ArrowUp, Clock, Globe, Save } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

interface DraftVersionInfo {
  versionNumber: number
  authorFirstName: string
  authorLastName: string
}

export interface RequestTypeDetailHeaderProps {
  draftVersionInfo?: DraftVersionInfo
  hasDraftChanges: boolean
  hasLocalChanges: boolean
  isPublished: boolean
  publishedVersionNumber?: number
  title: string
  onPublish: () => void
  onSave: () => void
  onVersionHistory?: () => void
}

export function RequestTypeDetailHeader({
  draftVersionInfo,
  hasDraftChanges,
  hasLocalChanges,
  isPublished,
  publishedVersionNumber,
  title,
  onPublish,
  onSave,
  onVersionHistory,
}: RequestTypeDetailHeaderProps) {
  const otherActions = onVersionHistory
    ? [
        {
          label: i18n.t('procurement.types.version_history.title'),
          icon: Clock,
          onClick: onVersionHistory,
        },
      ]
    : undefined

  const statusText = (() => {
    const base = isPublished
      ? i18n.t('procurement.types.forms.status.published')
      : i18n.t('procurement.types.forms.status.draft')
    if (isPublished && publishedVersionNumber != null) {
      const versionLabel = i18n.t('procurement.types.version_history.version_label', {
        versionNumber: publishedVersionNumber,
      })
      return `${base} · ${versionLabel}`
    }
    return base
  })()

  const metadata = (() => {
    if (!draftVersionInfo) return undefined
    const authorName = `${draftVersionInfo.authorFirstName} ${draftVersionInfo.authorLastName}`
    return [
      {
        label: i18n.t('procurement.types.version_history.editing_version', {
          versionNumber: draftVersionInfo.versionNumber,
          authorName,
        }),
        hideLabel: true,
        value: {
          type: 'status' as const,
          label: i18n.t('procurement.types.version_history.editing_version', {
            versionNumber: draftVersionInfo.versionNumber,
            authorName,
          }),
          variant: 'neutral' as const,
        },
      },
    ]
  })()

  return (
    <>
      <ResourceHeader
        title={title}
        primaryAction={{
          label: isPublished
            ? i18n.t(
                'procurement.purchase_requests.settings.types.modals.publish.buttons.publish_changes'
              )
            : i18n.t('common.publish'),
          icon: isPublished ? ArrowUp : Globe,
          onClick: onPublish,
          disabled: (!hasDraftChanges && isPublished) || hasLocalChanges,
          tooltip: hasLocalChanges
            ? i18n.t('procurement.types.publish.has_local_changes')
            : !hasDraftChanges && isPublished
              ? i18n.t('procurement.types.publish.no_draft_changes')
              : undefined,
        }}
        secondaryActions={[
          {
            label: i18n.t('common.save'),
            onClick: onSave,
            disabled: !hasLocalChanges,
            icon: Save,
            tooltip: !hasLocalChanges
              ? i18n.t('procurement.types.save.no_changes_tooltip')
              : undefined,
          },
        ].filter(Boolean)}
        otherActions={otherActions}
        status={{
          label: i18n.t('procurement.types.forms.status.label'),
          text: statusText,
          variant: isPublished ? 'positive' : 'neutral',
        }}
        metadata={metadata}
      />
    </>
  )
}
